/** @jsxImportSource @emotion/react */
import { Formik } from 'formik'
import moment from 'moment/moment'
import * as Yup from 'yup'

import { useSetOrderFollowUpDateMutation } from 'store/services/labOrder'
import { useSendLabNoteMutation } from 'store/services/notes'
import { FollowUpNamespace, Order } from 'store/types'
import { FollowUpForm } from './FollowUpForm'
import { Notes } from './Notes';

const formValidation = Yup.object().shape({
  followUpDate: Yup.date().required('Required'),
  notes: Yup.string(),
  humanFollowUpRequired: Yup.boolean(),
  humanFollowUpReason: Yup.string().when('humanFollowUpRequired', {
    is: true,
    then: Yup.string().trim().required('Required when human follow-up is selected'),
  }),
})

interface IProps {
  order: Order
  namespace?: FollowUpNamespace
  namespaces?: FollowUpNamespace[]
}

export const FollowUpSection = ({
  order,
  namespace = FollowUpNamespace.HST_PATIENT_CONSENT,
  namespaces,
}: IProps) => {
  const [setOrderFollowUpDate] = useSetOrderFollowUpDateMutation()
  const [sendLabNote] = useSendLabNoteMutation()
  const visibleNamespaces = namespaces ?? [namespace]

  const followUps = [...(order?.followUpsList ?? [])]
    .filter((f) => visibleNamespaces.includes(f.namespace))
    .sort((a, b) => moment(b.addedAt).diff(moment(a.addedAt)))

  const followUpDate = followUps?.[0]?.date ? moment(followUps?.[0]?.date).toDate() : ''
  const latestFollowUp = followUps?.[0]

  return (
    <div className="row mt-2" css={{ background: '#F5F5F5' }}>
      <div className="col-lg-4 pt-3">
        <Formik
          enableReinitialize={true}
          validationSchema={formValidation}
          initialValues={{
            followUpDate,
            notes: '',
            humanFollowUpRequired: Boolean(order?.patient?.voiceOptOut) || (latestFollowUp?.humanFollowUpRequired ?? false),
            humanFollowUpReason:
              latestFollowUp?.humanFollowUpReason ||
              (order?.patient?.voiceOptOut ? 'Patient opted out of automated voice calls' : ''),
          }}
          onSubmit={async (values, { resetForm }) => {
            await Promise.all([
              setOrderFollowUpDate({
                orderId: Number(order.id),
                namespace: latestFollowUp?.namespace ?? namespace,
                date: moment(values.followUpDate).format('YYYY-MM-DD'),
                comment: values.notes,
                humanFollowUpRequired: values.humanFollowUpRequired,
                humanFollowUpReason: values.humanFollowUpRequired
                  ? values.humanFollowUpReason
                  : null,
              }).unwrap(),
              await sendLabNote({
                orderId: Number(order.id),
                text: `${values.notes} (follow-up: ${moment(values.followUpDate).format(
                  'MM/DD/YYYY',
                )})`,
                type: 'scheduling',
              }).unwrap(),
            ])
            resetForm()
          }}>
          {({ values, setFieldValue, errors, touched }) => (
            <FollowUpForm
              {...{ values, setFieldValue, errors, touched }}
              forceHumanFollowUp={Boolean(order?.patient?.voiceOptOut)}
            />
          )}
        </Formik>
      </div>
      <div className="col-lg-8 p-3">
        <Notes
          followUps={followUps}
          orderId={Number(order.id)}
          isAutoSmsAvailable={true}
          isFollowUpSection={true}
        />
      </div>
    </div>
  )
}
